import { useEffect, useState } from 'react';
import { Box, Button, Skeleton, Stack, Typography, alpha } from '@mui/material';
import { BlockRounded, EmailOutlined } from '@mui/icons-material';
import { getIemsContactEmails } from '../../../../helpers/api';

const AI_COLOR = '#6a3fb5';
const EMAIL_SUBJECT = 'Request: enable AI Reporting for my facility';

const mailto = (emails) =>
  `mailto:${emails.join(',')}?subject=${encodeURIComponent(EMAIL_SUBJECT)}`;

/**
 * Shown on a facility's AI Report tab when AI reporting is switched off for the user
 * (aiReport access with reasonCode "DISABLED"). Lists the IEMS admin contact email(s) from
 * GET /api/Setting/IemsContactEmail so the user can ask for it to be enabled.
 *
 * reason: the server's own explanation, shown as detail
 */
export default function AiReportingNotProvisionedNotice({ reason }) {
  const [emails, setEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(true);

  useEffect(() => {
    let active = true;
    getIemsContactEmails()
      .then((list) => active && setEmails(list))
      .catch(() => active && setEmails([])) // no contacts: the message still tells them what to do
      .finally(() => active && setLoadingEmails(false));
    return () => {
      active = false;
    };
  }, []);

  return (
    <Box
      sx={{
        mb: 3,
        p: { xs: 3, md: 4 },
        borderRadius: '16px',
        border: '1px solid',
        borderColor: alpha(AI_COLOR, 0.25),
        bgcolor: alpha(AI_COLOR, 0.04),
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems="flex-start">
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(AI_COLOR, 0.12),
            color: AI_COLOR,
            flexShrink: 0,
          }}
        >
          <BlockRounded />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            This facility is not provisioned for AI Reporting
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            AI-written reports aren&apos;t available here yet. Please contact your administrator to
            have AI Reporting enabled for this facility.
          </Typography>
          {reason && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Details: {reason}
            </Typography>
          )}

          {/* Admin contact email(s) */}
          {loadingEmails ? (
            <Skeleton variant="rounded" width={220} height={32} sx={{ mt: 2, borderRadius: '8px' }} />
          ) : (
            emails.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  Contact the IEMS administrator
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {emails.map((email) => (
                    <Button
                      key={email}
                      size="small"
                      variant="outlined"
                      startIcon={<EmailOutlined />}
                      href={mailto([email])}
                      sx={{ borderRadius: '8px', bgcolor: '#fff', textTransform: 'none' }}
                    >
                      {email}
                    </Button>
                  ))}
                  {emails.length > 1 && (
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      startIcon={<EmailOutlined />}
                      href={mailto(emails)}
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        bgcolor: AI_COLOR,
                        '&:hover': { bgcolor: '#55309a' },
                      }}
                    >
                      Email all
                    </Button>
                  )}
                </Stack>
              </Box>
            )
          )}
        </Box>
      </Stack>
    </Box>
  );
}
